import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import api from '../../api/axios';
import { Users, GraduationCap, Building2, Check, Search, Trash2, Calendar, Mail, CheckCircle2, AlertCircle, ArrowLeft, Settings2, ClipboardList, BarChart3 } from 'lucide-react';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
};

export default function AdminBulkEnroll() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [method, setMethod] = useState('filter');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('assign');
  const [enrollments, setEnrollments] = useState([]);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [enrollSearch, setEnrollSearch] = useState('');
  const [enrollCourse, setEnrollCourse] = useState('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');

  const [deleteModal, setDeleteModal] = useState({ open: false, batchId: null, courseTitle: '' });
  const [successModal, setSuccessModal] = useState(false);
  const [mailProgressModal, setMailProgressModal] = useState(false);

  useEffect(() => {
    api.get('/courses').then(r => setCourses(r.data));
    api.get('/users').then(r => setUsers(r.data.filter(u => u.role === 'student' || u.role === 'manager')));
  }, []);

  const companies = [...new Set(users.map(u => u.company).filter(Boolean))].sort();
  const departments = [...new Set(users.filter(u => !filterCompany || u.company === filterCompany).map(u => u.department).filter(Boolean))].sort();

  const filteredUsers = users.filter(u => {
    if (method === 'filter') {
      if (filterCompany && u.company !== filterCompany) return false;
      if (filterDept && u.department !== filterDept) return false;
    }
    if (search && !u.name.toLowerCase().includes(search.toLowerCase()) && !u.username.includes(search)) return false;
    return true;
  });

  const toggleUser = (id) => setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedUsers(selectedUsers.length === filteredUsers.length ? [] : filteredUsers.map(u => u.id));

  const deleteEnrollmentBatch = (batchId, courseTitle) => {
    setDeleteModal({ open: true, batchId, courseTitle });
  };

  const confirmDeleteEnrollmentBatch = async () => {
    try {
      await api.delete(`/courses/enrollments/batch/${deleteModal.batchId}`);
      setEnrollments(prev => prev.filter(e => e.id !== deleteModal.batchId));
      setDeleteModal({ open: false, batchId: null, courseTitle: '' });
      setSuccessModal(true);
    } catch (e) {
      setDeleteModal({ open: false, batchId: null, courseTitle: '' });
      alert('Atama silinemedi: ' + (e.response?.data?.error || e.message));
    }
  };

  const loadEnrollments = async () => {
    setEnrollLoading(true);
    try {
      const r = await api.get('/courses/enrollments/list');
      setEnrollments(r.data);
    } catch (e) {
      alert('Atamalar yüklenemedi: ' + e.message);
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!selectedCourse) { alert('Lütfen eğitim seçin'); return; }
    if (isMandatory && !dueDate) { alert('Zorunlu eğitim için bitiş tarihi gereklidir'); return; }

    setLoading(true);
    setResult(null);

    try {
      const body = method === 'filter'
        ? { company: filterCompany || undefined, department: filterDept || undefined, is_mandatory: isMandatory, start_date: startDate || undefined, due_date: dueDate || undefined }
        : { user_ids: selectedUsers, is_mandatory: isMandatory, start_date: startDate || undefined, due_date: dueDate || undefined };

      const r = await api.post('/courses/' + selectedCourse + '/enroll-bulk', body);
      setResult(r.data);
      setMailProgressModal(true);
    } catch(e) {
      alert('Hata: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const selectedCourseName = courses.find(c => c.id === selectedCourse)?.title;

  return (
    <AdminLayout>
      <div className="page-container">

        {/* Üst Sekmeler */}
        <div className="tabs-container">
          {[['assign', ClipboardList, 'Eğitim Ata'], ['list', BarChart3, 'Mevcut Atamalar']].map(([key, Icon, label]) => (
            <button key={key} onClick={() => { setActiveTab(key); if (key === 'list') loadEnrollments(); }} className={`tab-btn ${activeTab === key ? 'active' : ''}`}>
              <Icon size={16} className="text-red" /> {label}
            </button>
          ))}
        </div>

        {/* --- ATAMA LİSTESİ (MEVCUT ATAMALAR) --- */}
        {activeTab === 'list' && (
          <div className="tab-content-anim">
            <div className="content-card">
              <div className="search-bar-container">
                <div className="search-input-wrapper max-w-400 w-full">
                  <Search size={16} className="search-icon" />
                  <input
                    value={enrollSearch}
                    onChange={e => setEnrollSearch(e.target.value)}
                    placeholder="Atanan eğitimi ara..."
                    className="search-input"
                  />
                </div>
                <div className="result-count">
                  Toplam <strong>{enrollments.filter(e => !enrollSearch || (e.course_title || '').toLowerCase().includes(enrollSearch.toLowerCase())).length}</strong> atama
                </div>
              </div>

              {enrollLoading ? (
                <div className="empty-state">Yükleniyor...</div>
              ) : (
                <div className="table-responsive">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        {['EĞİTİM', 'ATANAN', 'ATAMA TARİHİ', 'BAŞLANGIÇ', 'BİTİŞ', 'TAMAMLANMA', 'DURUM'].map(h => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.filter(e => !enrollSearch || (e.course_title || '').toLowerCase().includes(enrollSearch.toLowerCase())).map((e, i) => {
                        const assigned = Number(e.assigned_count || 0);
                        const completed = Number(e.completed_count || 0);
                        const percent = assigned > 0 ? Math.round((completed / assigned) * 100) : 0;
                        const isExpired = e.due_date && new Date(e.due_date) < new Date();

                        return (
                          <tr key={e.id || i}>
                            <td className="font-medium text-dark">
                              <div className="d-flex align-center justify-between gap-3">
                                <span>{e.course_title}</span>
                                <div className="d-flex align-center gap-2">
                                  {e.is_mandatory && <span className="badge badge-mandatory">ZORUNLU</span>}
                                  <button onClick={() => deleteEnrollmentBatch(e.id, e.course_title)} className="btn-icon-danger" title="Atamayı Sil">
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="fw-bold">{assigned} <span className="fw-normal text-muted">kişi</span></td>
                            <td className="text-muted">{e.assigned_at ? new Date(e.assigned_at).toLocaleDateString('tr-TR') : '—'}</td>
                            <td className="text-muted">{e.start_date ? new Date(e.start_date).toLocaleDateString('tr-TR') : '—'}</td>
                            <td className={isExpired ? 'text-red fw-bold' : 'text-muted'}>{e.due_date ? new Date(e.due_date).toLocaleDateString('tr-TR') : '—'}</td>
                            <td style={{ minWidth: 150 }}>
                              <div className="d-flex align-center gap-2">
                                <div className="progress-track flex-1">
                                  <div className="progress-fill bg-green" style={{ width: percent + '%' }} />
                                </div>
                                <span className="text-small text-muted">{completed}/{assigned}</span>
                              </div>
                            </td>
                            <td>
                              {assigned > 0 && completed === assigned 
                                ? <span className="badge status-active">✓ Tamamlandı</span> 
                                : <span className="badge status-warning">Devam Ediyor</span>}
                            </td>
                          </tr>
                        );
                      })}
                      {enrollments.length === 0 && (
                        <tr><td colSpan="7" className="empty-state">Henüz yapılmış bir eğitim ataması bulunmuyor.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- EĞİTİM ATA (YENİ ATAMA) --- */}
        {activeTab === 'assign' && (
          <div className="tab-content-anim">
            <div className="d-flex align-center gap-3 mb-4">
              <button onClick={() => navigate('/yonetim/egitimler')} className="btn-outline">
                <ArrowLeft size={16} /> Geri
              </button>
              <h2 className="page-title m-0">Eğitim Atama</h2>
            </div>

            {result && (
              <div className="alert-success mb-4">
                <CheckCircle2 size={20} />
                <div>
                  <div className="fw-bold">Atama tamamlandı!</div>
                  <div className="text-small mt-1">{result.total} kullanıcıdan <strong>{result.enrolled}</strong> tanesi <strong>{selectedCourseName}</strong> eğitimine atandı.</div>
                </div>
              </div>
            )}

            <div className="grid-layout">
              {/* Sol Kolon: Eğitim Seçimi ve Ayarlar */}
              <div className="d-flex flex-col gap-4">
                <div className="content-card p-4">
                  <div className="card-heading d-flex align-center gap-2 mb-4">
                    <GraduationCap size={18} className="text-red" /> <span>1. Eğitim Seç</span>
                  </div>
                  <select value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)} className="form-input py-3">
                    <option value="">Eğitim seçin...</option>
                    {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </div>

                <div className="content-card p-4">
                  <div className="card-heading d-flex align-center gap-2 mb-4">
                    <Settings2 size={18} className="text-red" /> <span>2. Eğitim Ayarları</span>
                  </div>

                  <div className={`mandatory-toggle ${isMandatory ? 'active' : ''}`} onClick={() => setIsMandatory(!isMandatory)}>
                    <div className="toggle-checkbox">
                      {isMandatory && <Check size={12} strokeWidth={4} color="white" />}
                    </div>
                    <div>
                      <div className="toggle-title">Zorunlu Eğitim</div>
                      <div className="toggle-desc">Kullanıcı dashboardında öncelikli olarak vurgulanır.</div>
                    </div>
                  </div>

                  <div className="form-grid mt-4">
                    <div className="form-group">
                      <label className="form-label d-flex align-center gap-1"><Calendar size={14}/> BAŞLANGIÇ TARİHİ</label>
                      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="form-input py-3" />
                    </div>
                    <div className="form-group">
                      <label className="form-label d-flex align-center gap-1">
                        <Calendar size={14}/> BİTİŞ TARİHİ {isMandatory && <span className="text-red">*</span>}
                      </label>
                      <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={`form-input py-3 ${isMandatory && !dueDate ? 'border-red' : ''}`} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sağ Kolon: Kullanıcı Seçimi */}
              <div className="content-card p-4">
                <div className="card-heading d-flex align-center gap-2 mb-4">
                  <Users size={18} className="text-red" /> <span>3. Atama Yöntemi ve Kişiler</span>
                </div>

                <div className="d-flex gap-2 mb-5">
                  {[['filter', Building2, 'Şirket / Müdürlük'], ['manual', Users, 'Manuel Seçim']].map(([key, Icon, label]) => (
                    <button key={key} onClick={() => { setMethod(key); setSelectedUsers([]); }} className={`method-btn ${method === key ? 'active' : ''}`}>
                      <Icon size={16} /> {label}
                    </button>
                  ))}
                </div>

                {method === 'filter' && (
                  <div className="filter-method-anim">
                    <div className="form-group" style={{ marginBottom: '24px' }}>
                      <label className="form-label text-muted">ŞİRKET</label>
                      <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterDept(''); }} className="form-input py-3">
                        <option value="">Tüm Şirketler</option>
                        {companies.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '28px' }}>
                      <label className="form-label text-muted">MÜDÜRLÜK</label>
                      <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="form-input py-3">
                        <option value="">Tüm Müdürlükler</option>
                        {departments.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    
                    <div className="info-banner mb-4">
                      Bu kriterlere uyan <strong>{filteredUsers.length}</strong> kullanıcı seçilecek.
                    </div>
                  </div>
                )}

                {method === 'manual' && (
                  <div className="manual-method-anim">
                    <div className="user-list-container">
                      <div className="user-list-header">
                        <div className="search-input-wrapper mb-2">
                          <Search size={14} className="search-icon" />
                          <input placeholder="Kişi ara..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" style={{ padding: '10px 12px 10px 34px' }} />
                        </div>
                        <div className="d-flex justify-between align-center px-1">
                          <button onClick={selectAll} className="btn-text text-primary">
                            {selectedUsers.length === filteredUsers.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                          </button>
                          <span className="text-small text-muted">{selectedUsers.length} seçili / {filteredUsers.length}</span>
                        </div>
                      </div>
                      <div className="user-list-body">
                        {filteredUsers.slice(0, 200).map(u => (
                          <div key={u.id} onClick={() => toggleUser(u.id)} className={`user-list-item ${selectedUsers.includes(u.id) ? 'selected' : ''}`}>
                            <div className="list-checkbox">
                              {selectedUsers.includes(u.id) && <Check size={12} strokeWidth={3} color="white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-dark text-truncate">{u.name}</div>
                              <div className="text-small text-muted text-truncate">{u.username} {u.company && `• ${u.company}`}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sadece bu buton Kırmızı (btn-red) yapıldı */}
                <button onClick={handleEnroll} disabled={loading || !selectedCourse || (method === 'manual' && selectedUsers.length === 0)} className="btn-red w-full mt-4 py-3" style={{ fontSize: '15px' }}>
                  <CheckCircle2 size={18} /> {loading ? 'Atanıyor...' : method === 'manual' ? `${selectedUsers.length} Kullanıcıya Ata` : 'Toplu Ata'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* --- MODALLAR --- */}
      
      {/* Silme Onay Modalı */}
      {deleteModal.open && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-container text-center" style={{ maxWidth: 420, padding: 32 }}>
            <div className="danger-icon-large"><Trash2 size={28} /></div>
            <h3 className="modal-title mb-2">Atamayı Sil</h3>
            <p className="text-muted mb-4">
              <strong>"{deleteModal.courseTitle}"</strong> eğitimine ait bu atamayı silmek istediğinize emin misiniz?
            </p>
            <div className="d-flex gap-2">
              <button onClick={() => setDeleteModal({ open: false, batchId: null, courseTitle: '' })} className="btn-cancel flex-1">İptal</button>
              <button onClick={confirmDeleteEnrollmentBatch} className="btn-danger flex-1">Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Mail Gönderim Modalı */}
      {mailProgressModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-container text-center" style={{ maxWidth: 440, padding: 32 }}>
            <div className="primary-icon-large"><Mail size={28} /></div>
            <h3 className="modal-title mb-2">Atama Başlatıldı</h3>
            <p className="text-muted mb-4">
              Eğitim atamaları başarıyla oluşturuldu.<br/>E-posta bildirimleri <strong>arka planda</strong> düzenli olarak gönderilmeye başlandı.
            </p>
            <div className="info-box mb-4 text-left">
              {result?.mail_queued || 0} kişiye e-posta kuyruğa alındı.<br/>Sistem e-postaları kontrollü şekilde gönderecektir.
            </div>
            <button onClick={() => setMailProgressModal(false)} className="btn-primary w-full">Tamam</button>
          </div>
        </div>
      )}

      {/* Başarı Modalı */}
      {successModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-container text-center" style={{ maxWidth: 400, padding: 32 }}>
            <div className="success-icon-large"><CheckCircle2 size={32} /></div>
            <h3 className="modal-title mb-2">İşlem Başarılı</h3>
            <p className="text-muted mb-4">Atama başarıyla silindi.</p>
            <button onClick={() => setSuccessModal(false)} className="btn-primary w-full">Tamam</button>
          </div>
        </div>
      )}

      {/* CSS Stilleri (Renkler Mavi/İndigo Temaya Geçirildi, İstek Üzerine Bazı Alanlar Kırmızı) */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

        * { -webkit-font-smoothing: antialiased; font-family: 'Poppins', sans-serif; }

        .page-container { animation: fadeSlideUp 0.4s ease-out; }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .tab-content-anim { animation: fadeIn 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .filter-method-anim { animation: slideDown 0.3s ease-out; }
        .manual-method-anim { animation: slideDown 0.3s ease-out; }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }

        /* Typography & Utils */
        .text-muted { color: #6b7280; }
        .text-dark { color: #111827; }
        .text-red { color: #e30613 !important; } /* Spesifik kırmızı ikonlar için force */
        .text-green { color: #16a34a; }
        .text-primary { color: #2563eb; }
        .font-medium { font-weight: 500; }
        .fw-bold { font-weight: 700; }
        .fw-normal { font-weight: 400; }
        .text-small { font-size: 12.5px; }
        .text-center { text-align: center; }
        .text-left { text-align: left; }
        .text-truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .w-full { width: 100%; }
        .flex-1 { flex: 1; }
        .min-w-0 { min-width: 0; }
        .max-w-400 { max-width: 400px; }
        .d-flex { display: flex; }
        .flex-col { flex-direction: column; }
        .align-center { align-items: center; }
        .justify-between { justify-content: space-between; }
        .gap-1 { gap: 4px; }
        .gap-2 { gap: 8px; }
        .gap-3 { gap: 12px; }
        .gap-4 { gap: 16px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-3 { margin-bottom: 12px; }
        .mb-4 { margin-bottom: 16px; }
        .mb-5 { margin-bottom: 24px; }
        .mt-1 { margin-top: 4px; }
        .mt-4 { margin-top: 16px; }
        .p-4 { padding: 16px; }
        .py-3 { padding-top: 12px; padding-bottom: 12px; }
        .px-1 { padding-left: 4px; padding-right: 4px; }
        .m-0 { margin: 0; }
        
        .bg-green { background: #16a34a; }

        /* Header & Tabs */
        .page-title { margin: 0; font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -0.5px; }
        .tabs-container { display: inline-flex; background: #f3f4f6; border-radius: 12px; padding: 4px; margin-bottom: 24px; }
        .tab-btn { padding: 8px 20px; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; gap: 8px; background: transparent; color: #6b7280; transition: all 0.2s; }
        .tab-btn:hover:not(.active) { color: #111827; }
        .tab-btn.active { background: #ffffff; color: #2563eb; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

        /* Layout Grid */
        .grid-layout { display: grid; grid-template-columns: 1fr; gap: 16px; }
        @media (min-width: 1024px) { .grid-layout { grid-template-columns: 1fr 1fr; } }

        /* Cards & Containers */
        .content-card { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); overflow: hidden; }
        .card-heading { font-weight: 700; font-size: 15px; color: #111827; }
        .info-banner { padding: 16px 20px; border-radius: 12px; background: #eff6ff; color: #1e3a8a; font-size: 13.5px; border: 1px solid #bfdbfe; }
        .info-banner strong { color: #2563eb; }
        .info-box { background: #f8fafc; border-radius: 12px; padding: 12px 16px; font-size: 13px; color: #475467; border: 1px solid #f1f5f9; }
        
        .alert-success { background: #ecfdf5; border: 1px solid #a7f3d0; color: #065f46; padding: 14px 18px; border-radius: 12px; font-size: 13.5px; display: flex; align-items: center; gap: 12px;}

        /* Form Inputs */
        .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        @media (max-width: 640px) { .form-grid { grid-template-columns: 1fr; } }
        .form-group { margin-bottom: 0; }
        .form-label { display: block; margin-bottom: 8px; font-weight: 600; font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;}
        .form-input { width: 100%; padding: 10px 14px; border: 1px solid #d0d5dd; border-radius: 10px; font-size: 14px; outline: none; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; box-sizing: border-box; background: white; color: #1e293b;}
        .form-input:focus { border-color: #2563eb; box-shadow: 0 0 0 4px #eff6ff; }
        .border-red { border-color: #e30613; }

        /* Arama Çubuğu */
        .search-bar-container { padding: 16px 20px; border-bottom: 1px solid #eaecf0; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; background: #fcfcfd; }
        .search-input-wrapper { position: relative; width: 100%; }
        .search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
        .search-input { width: 100%; padding: 10px 14px 10px 38px; border: 1px solid #d0d5dd; border-radius: 10px; font-size: 14px; outline: none; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; background: white; }
        .search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 4px #eff6ff; }
        .result-count { font-size: 13px; color: #6b7280; }

        /* Buttons */
        .btn-primary { background: #2563eb; color: white; border: 1px solid #2563eb; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
        .btn-primary:hover:not(:disabled) { background: #1d4ed8; border-color: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2); }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; background: #94a3b8; border-color: #94a3b8;}

        .btn-red { background: #e30613; color: white; border: 1px solid #e30613; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
        .btn-red:hover:not(:disabled) { background: #ba000a; border-color: #ba000a; transform: translateY(-1px); box-shadow: 0 4px 6px rgba(227, 6, 19, 0.2); }
        .btn-red:disabled { opacity: 0.6; cursor: not-allowed; background: #9ca3af; border-color: #9ca3af;}
        
        .btn-danger { background: #e30613; color: white; border: 1px solid #e30613; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 14px; transition: all 0.2s; }
        .btn-danger:hover { background: #ba000a; border-color: #ba000a; }

        .btn-outline { background: #ffffff; border: 1px solid #d0d5dd; color: #344054; border-radius: 10px; padding: 9px 16px; cursor: pointer; font-size: 13.5px; font-weight: 600; display: flex; align-items: center; gap: 6px; transition: all 0.2s; box-shadow: 0 1px 2px rgba(16,24,40,0.05);}
        .btn-outline:hover { background: #f9fafb; border-color: #98a2b3; color: #111827;}

        .btn-cancel { padding: 10px 20px; border: 1px solid #d0d5dd; background: #ffffff; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 14px; color: #344054; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
        .btn-cancel:hover { background: #f9fafb; border-color: #98a2b3; }
        
        .btn-icon-danger { background: #fef2f2; color: #ef4444; border: 1px solid #fee2e2; border-radius: 8px; padding: 6px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .btn-icon-danger:hover { background: #fee2e2; border-color: #fca5a5; }

        .btn-text { background: transparent; border: none; font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 0;}
        .btn-text:hover { text-decoration: underline; }

        .method-btn { flex: 1; padding: 12px 10px; border: 1.5px solid #e5e7eb; border-radius: 10px; background: white; cursor: pointer; font-weight: 600; font-size: 13.5px; color: #6b7280; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; }
        .method-btn:hover:not(.active) { background: #f9fafb; border-color: #d0d5dd; color: #374151;}
        .method-btn.active { background: #eff6ff; border-color: #93c5fd; color: #2563eb; }

        /* Custom Toggle (Zorunlu Eğitim) */
        .mandatory-toggle { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 12px; border: 1.5px solid #e5e7eb; background: #f9fafb; cursor: pointer; transition: all 0.2s; }
        .mandatory-toggle:hover { border-color: #d0d5dd; }
        .mandatory-toggle.active { background: #fff1f2; border-color: #fca5a5; } /* Burası Kırmızı Kalmalı */
        .toggle-checkbox { width: 20px; height: 20px; border-radius: 6px; border: 2px solid #d1d5db; background: white; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0;}
        .mandatory-toggle.active .toggle-checkbox { border-color: #e30613; background: #e30613; }
        .toggle-title { font-weight: 600; font-size: 13.5px; color: #475467; transition: color 0.2s;}
        .mandatory-toggle.active .toggle-title { color: #e30613; }
        .toggle-desc { font-size: 11.5px; color: #9ca3af; margin-top: 2px;}

        /* Manual User List */
        .user-list-container { background: #f9fafb; border-radius: 12px; border: 1px solid #eaecf0; overflow: hidden; display: flex; flex-direction: column; }
        .user-list-header { padding: 12px 14px; border-bottom: 1px solid #eaecf0; background: white; }
        .user-list-body { max-height: 240px; overflow-y: auto; }
        .user-list-item { padding: 10px 14px; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid #f2f4f7; cursor: pointer; background: white; transition: background 0.2s; }
        .user-list-item:hover { background: #f9fafb; }
        .user-list-item.selected { background: #eff6ff; }
        .list-checkbox { width: 18px; height: 18px; border-radius: 5px; border: 2px solid #d1d5db; background: white; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s;}
        .user-list-item.selected .list-checkbox { border-color: #2563eb; background: #2563eb; }

        /* Progress Bar */
        .progress-track { height: 6px; background: #e5e7eb; border-radius: 4px; overflow: hidden; width: 100%; }
        .progress-fill { height: 100%; border-radius: 4px; transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1); }

        /* Table */
        .table-responsive { overflow-x: auto; }
        .modern-table { width: 100%; border-collapse: collapse; min-width: 900px; }
        .modern-table th { padding: 14px 20px; text-align: left; font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; background: #f9fafb; border-bottom: 1px solid #eaecf0; }
        .modern-table td { padding: 14px 20px; border-bottom: 1px solid #eaecf0; vertical-align: middle; transition: background 0.2s; font-size: 13.5px;}
        .modern-table tr:hover td { background: #f9fafb; }
        .modern-table tr:last-child td { border-bottom: none; }

        /* Badges */
        .badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; white-space: nowrap;}
        .status-active { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
        .status-warning { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; }
        .badge-mandatory { background: #fef2f2; color: #ef4444; border: 1px solid #fecaca; }

        /* Modals */
        .modal-overlay { position: fixed; inset: 0; background: rgba(16, 24, 40, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
        .modal-container { background: #ffffff; border-radius: 20px; width: 100%; box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08); animation: modalPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); display: flex; flex-direction: column; overflow: hidden;}
        @keyframes modalPop { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .modal-title { margin: 0; font-size: 19px; font-weight: 700; color: #111827; }
        
        .success-icon-large { width: 64px; height: 64px; border-radius: 50%; background: #ecfdf5; color: #10b981; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
        .danger-icon-large { width: 64px; height: 64px; border-radius: 50%; background: #fef2f2; color: #ef4444; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
        .primary-icon-large { width: 64px; height: 64px; border-radius: 50%; background: #eff6ff; color: #2563eb; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
        
        .empty-state { text-align: center; padding: 40px !important; color: #6b7280; font-size: 14px; font-style: italic; }
      `}</style>
    </AdminLayout>
  );
}
